
import { useState, useEffect } from "react";
import { ResourceFile } from "../types";
import { isQuizFile } from "../utils/fileTypeUtils";
import { useCompletionMarker } from "@/hooks/useCompletionMarker";

export function useTrainingInteractions(
  resourceId: string,
  currentFile: ResourceFile | undefined, 
  currentFileIndex: number,
  isTrainee: boolean, 
  isAdmin: boolean,
  setCompletedFiles: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>,
  fetchFreeResponseQuestions: (fileId: string) => Promise<void>,
  markFileCompleted: (fileId: string) => Promise<void>
) {
  const [quiz, setQuiz] = useState<any>(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizAttempt, setQuizAttempt] = useState<any>(null);
  const [hasAttemptedQuizFetch, setHasAttemptedQuizFetch] = useState(false);
  const [hasAttemptedFreeResponseFetch, setHasAttemptedFreeResponseFetch] = useState(false);
  const [quizError, setQuizError] = useState<string | null>(null);
  
  // Effect for handling quiz fetching
  useEffect(() => {
    if (currentFile && isQuizFile(currentFile) && !hasAttemptedQuizFetch) {
      // Set up fetch state
      setIsLoadingQuiz(true);
      setHasAttemptedQuizFetch(true);
      
      // In real implementation, this would fetch the quiz data
      console.log('Would fetch quiz data for file:', currentFile.id);
      
      // Simulate quiz load completion
      setTimeout(() => {
        setIsLoadingQuiz(false);
        
        // If there's quiz_id in the file, set quiz data
        if (currentFile.quiz_id) {
          console.log('Quiz ID found:', currentFile.quiz_id);
          // Here we would set the quiz data
        } else {
          console.log('No quiz_id found in file');
          setQuizError("No quiz found for this file");
        }
      }, 1000);
    }
  }, [currentFile, hasAttemptedQuizFetch]);
  
  // Function to handle quiz completion
  const handleQuizComplete = async (quizId: string, attempt: any) => {
    if (!currentFile) return;
    
    setQuizCompleted(true);
    setQuizAttempt(attempt);
    
    if (isTrainee && !isAdmin) {
      try {
        await markFileCompleted(currentFile.id);
        
        // Update completedFiles in parent component
        setCompletedFiles(prev => ({
          ...prev,
          [currentFile.id]: true
        }));
      } catch (error) {
        console.error('Error marking file as completed:', error);
      }
    }
  };
  
  // Function to handle drag/drop completion
  const handleDragDropComplete = async (fileId: string, attempt: any) => {
    if (!currentFile) return;
    
    if (isTrainee && !isAdmin) {
      try {
        await markFileCompleted(currentFile.id);
        
        // Update completedFiles in parent component
        setCompletedFiles(prev => ({
          ...prev,
          [currentFile.id]: true
        }));
      } catch (error) {
        console.error('Error marking file as completed:', error);
      }
    }
  };
  
  // Function to handle regular content completion
  const handleContentComplete = async () => {
    if (!currentFile) return;
    
    if (isTrainee && !isAdmin) {
      try {
        await markFileCompleted(currentFile.id);
        
        // Update completedFiles in parent component
        setCompletedFiles(prev => ({
          ...prev,
          [currentFile.id]: true
        }));
      } catch (error) {
        console.error('Error marking file as completed:', error);
      }
    }
  };
  
  return {
    quiz,
    isLoadingQuiz,
    quizCompleted,
    quizAttempt,
    quizError,
    hasAttemptedFreeResponseFetch,
    setHasAttemptedFreeResponseFetch,
    handleQuizComplete,
    handleDragDropComplete,
    handleContentComplete
  };
}
